import { useState, useEffect } from 'react';

/*  tweddi — iOS "Add to Home Screen" prompt
    tweddi identity: cream ground, Inclusive Sans, near-mono ink, the woven-rings
    monogram as the app-icon tile (cream mark on the ink tile), numbered steps,
    circle-arrow CTA. Logic 100% preserved (iOS detection, 1.5s delay,
    iosInstallDismissed localStorage, permanent dismiss).                     */

const CREAM = '#FFFDF8';
const WASH  = '#FBF6EF';
const INK   = '#141313';
const MUTE  = '#6E6B62';

// Detects iPhone/iPad in Safari, not already installed
const shouldShow = () => {
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
  const dismissed = localStorage.getItem('iosInstallDismissed') === 'true';
  return ios && !standalone && !dismissed;
};

export default function IOSInstallPrompt() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (shouldShow()) {
      const t = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(t);
    }
  }, []);

  const handleDismiss = (permanent = false) => {
    setVisible(false);
    if (permanent) localStorage.setItem('iosInstallDismissed', 'true');
  };

  if (!visible) return null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inclusive+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap');
        .ios-overlay { position: fixed; inset: 0; background: rgba(20,19,19,0.35); z-index: 200; animation: ios-fade-in 0.3s ease both; }
        @keyframes ios-fade-in { from { opacity: 0; } to { opacity: 1; } }
        .ios-sheet {
          position: fixed; bottom: 0; left: 50%; transform: translateX(-50%);
          width: 100%; max-width: 28rem;
          background: linear-gradient(180deg, ${CREAM} 70%, ${WASH} 100%);
          z-index: 201; animation: ios-slide-up 0.45s cubic-bezier(0.16,1,0.3,1) both;
          border-top-left-radius: 1.5rem; border-top-right-radius: 1.5rem; overflow: hidden;
        }
        @keyframes ios-slide-up { from { transform: translateX(-50%) translateY(100%); } to { transform: translateX(-50%) translateY(0); } }
        .ios-handle { width: 2.5rem; height: 0.25rem; background: rgba(20,19,19,0.14); border-radius: 9999px; margin: 0.875rem auto 0; }
        .ios-body { padding: 1.25rem 1.75rem 2.5rem; }
        .ios-top { display: flex; align-items: center; gap: 1rem; margin-bottom: 1.75rem; }
        .ios-app-icon {
          width: 3.5rem; height: 3.5rem; border-radius: 0.9rem; overflow: hidden;
          flex-shrink: 0; background: ${INK}; display: flex; align-items: center;
          justify-content: center; box-shadow: 0 4px 16px rgba(20,19,19,0.12);
        }
        .ios-app-name { font-family: 'Inclusive Sans', sans-serif; font-size: 1.15rem; font-weight: 600; color: ${INK}; letter-spacing: -0.02em; margin: 0 0 0.15rem; line-height: 1; }
        .ios-app-url { font-family: 'Inclusive Sans', sans-serif; font-size: 0.6rem; font-weight: 500; color: ${MUTE}; letter-spacing: 0.02em; margin: 0; }
        .ios-headline { font-family: 'Inclusive Sans', sans-serif; font-size: 1.9rem; font-weight: 600; color: ${INK}; letter-spacing: -0.03em; margin: 0 0 0.5rem; line-height: 1.04; }
        .ios-sub { font-family: 'Inclusive Sans', sans-serif; font-size: 0.72rem; font-weight: 500; color: ${MUTE}; letter-spacing: 0; line-height: 1.65; margin: 0 0 1.75rem; }
        .ios-steps { display: flex; flex-direction: column; border: 1px solid rgba(20,19,19,0.1); border-radius: 1rem; overflow: hidden; margin-bottom: 1.75rem; }
        .ios-step { display: flex; align-items: center; gap: 1rem; padding: 0.95rem 1.1rem; border-bottom: 1px solid rgba(20,19,19,0.07); }
        .ios-step:last-child { border-bottom: none; }
        .ios-step-num { width: 1.6rem; height: 1.6rem; background: ${INK}; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-family: 'Inclusive Sans', sans-serif; font-size: 0.62rem; font-weight: 700; color: ${CREAM}; }
        .ios-step-text { font-family: 'Inclusive Sans', sans-serif; font-size: 0.72rem; font-weight: 500; color: ${INK}; letter-spacing: 0; line-height: 1.4; flex: 1; }
        .ios-step-text strong { font-weight: 700; color: ${INK}; }
        .ios-share-icon { display: inline-block; vertical-align: middle; margin: 0 0.15rem; position: relative; top: -1px; }
        .ios-cta {
          width: 100%; height: 3.4rem; background: ${INK}; color: ${CREAM}; border: none;
          border-radius: 9999px; font-family: 'Inclusive Sans', sans-serif; font-size: 0.66rem;
          font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; cursor: pointer;
          transition: opacity 0.2s ease; margin-bottom: 0.5rem;
          display: inline-flex; align-items: center; justify-content: center; gap: 0.6rem;
        }
        .ios-cta:hover { opacity: 0.9; }
        .ios-cta:active { opacity: 0.8; }
        .ios-cta .ios-circle { transition: transform 0.25s cubic-bezier(.16,1,.3,1); display: inline-flex; }
        .ios-cta:hover .ios-circle { transform: translateX(3px); }
        .ios-dismiss { width: 100%; background: none; border: none; font-family: 'Inclusive Sans', sans-serif; font-size: 0.62rem; font-weight: 500; color: ${MUTE}; letter-spacing: 0.02em; cursor: pointer; padding: 0.6rem 0; transition: color 0.2s ease; }
        .ios-dismiss:hover { color: ${INK}; }
      `}</style>
      {/* Backdrop */}
      <div className="ios-overlay" onClick={() => handleDismiss(false)} />
      {/* Bottom sheet */}
      <div className="ios-sheet">
        <div className="ios-handle" />
        <div className="ios-body">
          <div className="ios-top">
            <div className="ios-app-icon">
              {/* woven monogram — cream mark on the ink tile (bg = ink) */}
              <svg viewBox="-60 -60 664 520" width="34" height={34*(520/664)} style={{ overflow: 'visible', display: 'block' }} aria-label="tweddi">
                <circle cx="200" cy="200" r="108" fill="none" stroke={CREAM} strokeWidth="66" />
                <circle cx="344" cy="200" r="108" fill="none" stroke={INK}   strokeWidth="108" />
                <circle cx="344" cy="200" r="108" fill="none" stroke={CREAM} strokeWidth="66" />
                <path d="M 206.01 307.83 A 108 108 0 0 0 282.00 270.28" fill="none" stroke={INK}   strokeWidth="108" />
                <path d="M 196.23 307.93 A 108 108 0 0 0 285.11 266.49" fill="none" stroke={CREAM} strokeWidth="66" />
              </svg>
            </div>
            <div>
              <p className="ios-app-name">tweddi</p>
              <p className="ios-app-url">{typeof window !== 'undefined' ? window.location.hostname.replace(/^www\./, '') : 'tweddi.com'}</p>
            </div>
          </div>
          <p className="ios-headline">Add to your<br/>home screen.</p>
          <p className="ios-sub">
            Install for instant access at the wedding — matches, messages and notifications work best from the home screen.
          </p>
          <div className="ios-steps">
            <div className="ios-step">
              <div className="ios-step-num">1</div>
              <div className="ios-step-text">
                Tap the <strong>Share</strong> button
                <span className="ios-share-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                    <polyline points="16 6 12 2 8 6"/>
                    <line x1="12" y1="2" x2="12" y2="15"/>
                  </svg>
                </span>
                at the bottom of Safari
              </div>
            </div>
            <div className="ios-step">
              <div className="ios-step-num">2</div>
              <div className="ios-step-text">
                Scroll down and tap <strong>Add to Home Screen</strong>
              </div>
            </div>
            <div className="ios-step">
              <div className="ios-step-num">3</div>
              <div className="ios-step-text">
                Tap <strong>Add</strong> in the top right corner
              </div>
            </div>
          </div>
          <button className="ios-cta" onClick={() => handleDismiss(false)}>
            Got it
            <span className="ios-circle">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={CREAM} strokeWidth="2"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
            </span>
          </button>
          <button className="ios-dismiss" onClick={() => handleDismiss(true)}>
            Don't show again
          </button>
        </div>
      </div>
    </>
  );
}
